import { Navigate } from 'react-router-dom';
import { useContext } from 'react';
import { userContext } from '../UserContext';

export type Role = 'admin' | 'hr' | 'employee';

type Props = {
  children: React.ReactNode;
  allowedRoles: Role[];
};

const normalizeRole = (role: unknown): Role | null => {
  if (typeof role !== 'string') return null;

  switch (role.toLowerCase()) {
    case 'admin':
      return 'admin';
    case 'hr':
      return 'hr';
    case 'employee':
      return 'employee';
    default:
      return null;
  }
};

const loadingScreen = (
  <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center">
    <div className="relative flex items-center justify-center h-12 w-12">
      {/* Rotating ring around logo */}
      <div
        className="absolute inset-0 rounded-full animate-spin"
        style={{
          background: 'conic-gradient(#6535EB, #9F3198, transparent 70%)',
          padding: '3px',
        }}
      >
        <div className="h-full w-full rounded-full bg-gray-50" />
      </div>

      <img
        src="/logo.png"
        alt="Loading"
        className="relative z-10 h-8 w-8 object-contain"
      />
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }: Props) => {
  const { user, loading } = useContext(userContext);

  if (loading) {
    return loadingScreen;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.verified === 'false') {
    return <Navigate to="/onboard" replace />;
  }

  if (user.verified === 'pending') {
    return <Navigate to="/inprocess" replace />;
  }

  const role = normalizeRole(user.role);

  if (!role) {
    return loadingScreen;
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
